import {
  Box,
  chakra,
  ChakraProps,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  ListIcon,
  MenuIcon,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { graphql, StaticQuery } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import React, { useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useEffect } from "react"
import { useForm, FormProvider, useFormContext } from "react-hook-form"
import { searchLocation } from "../../api/location"
import CrossIcon from "../../icons/Cross"
import { LocationIcon } from "../../icons/Location"
import { Location } from "../../types/location"
import { getCountryFromCode, trimCityEmpty } from "../../utils/misc"
import { CountryFlagsLazy } from "../Misc/CountryFlagsLazy"
import { Portal } from "../Misc/Portal"

export const LocationTextIntl = ({ location }: { location: Location }) => {
  const intl = useIntl()
  switch (intl.locale) {
    case "es":
      return (
        <>
          {trimCityEmpty(location.translations?.es)}{" "}
          <Text ml="auto" as="span" variant="secondary">
            {location.translations_country.es}
          </Text>
        </>
      )
    case "pt":
      return (
        <>
          {trimCityEmpty(location.translations?.pt)}{" "}
          <Text ml="auto" as="span" variant="secondary">
            {location.translations_country.pt}
          </Text>
        </>
      )
    default:
      return (
        <>
          {trimCityEmpty(location.city)}{" "}
          <Text ml="auto" as="span" variant="secondary">
            {location.country}
          </Text>
        </>
      )
  }
}
const isSSR = typeof document === "undefined"
let a = null

export const LocationAutoComplete = chakra(
  ({
    className,
    inputProps,
    resultProps,
    error_msg,
    cityOnly,
    label,
    defaultValues,
  }: {
    label: string
    defaultValues?: { id: any; name: any }
    error_msg: string
    cityOnly?: boolean
    className: any
    inputProps: ChakraProps
    resultProps: ChakraProps
  }) => {
    const { register, setValue,getValues} = useFormContext()
    const [expanded, setExpanded] = useState(false)
    const intl = useIntl()
    const [searching, setSearching] = useState(false)
    const [results, setResults] = useState([])
    useEffect(() => {
      if (defaultValues) {
        setValue(inputProps.name + "_id", defaultValues.id)
        setValue(inputProps.name, defaultValues.name)
      }else{
        setValue(inputProps.name + "_id", undefined)
        setValue(inputProps.name,undefined)
      }
    }, [])
    function selectHandler(e) {
      const [id, title] = [
        e.currentTarget.getAttribute("data-value"),
        e.currentTarget.getAttribute("data-title"),
      ]
      setValue(inputProps.name + "_id", id)
      setValue(inputProps.name, title)
      setExpanded(false)
    }
    const fetchData = value => {
      searchLocation(value, intl.locale, cityOnly)
        .then(({ data }) => {
          if (Array.isArray(data)) {
            setResults(data)
          }
          return data
        })
        .catch(() => {})
        .finally(() => {
          setSearching(false)
        })
    }
    const clear = () => {
      setExpanded(false)
      setResults([])
      setValue(inputProps.name, undefined)
      setValue(inputProps.name + "_id", undefined)
    }
    const searchHandler = ({ target: { value } }) => {
      if (!value.length) clear()
      else if (!expanded) setExpanded(true)
      if (a) clearTimeout(a)
      a = setTimeout(fetchData, 400, value)
      setSearching(true)
    }

    return (
      <Box
        aria-expanded={expanded}
        pos="relative"
        bottom="0"
        left="0"
        right="0"
        w="100%"
        sx={{
          ".results-autocomplete": {
            pointerEvents: "none",
            transform: "scale(0.9,0.9)",
            opacity: 0,
          },
          _expanded: {
            zIndex: 51,
            fontSize: [600, 600, 500],
            pos: ["fixed", "fixed", "relative"],
            bg: ["white", "white", "unset"],
            h: ["calc(100% - 50px)", "calc(100% - 50px)", "auto"],
            boxShadow: ["elevation_3", "elevation_3", "none"],
            ".input-autocomplete": {
              py: [5, 5, 0],
              px: [5, 5, 0],
              borderBottomWidth: ["1px", "1px", "0"],
            },
            ".results-autocomplete": {
              pos: ["static", "static", "absolute"],
              maxH: ["unset", "unset", "50vh"],
              h: ["100%", "100%", "auto"],
              opacity: 1,
              pointerEvents: "auto",
              transform: "scale(1,1)",
            },
          },
        }}
        transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
        className={className}
      >
        <Text
          textAlign="center"
          fontWeight="600"
          mt={2}
          as="label"
          d={["block", "block", "none"]}
          hidden={!expanded}
        >
          {label}
        </Text>
        <InputGroup className="input-autocomplete" fontSize="inherit">
          <Input
            onFocus={() => {
              setExpanded(true)
            }}
            aria-haspopup="listbox"
            autoComplete="off"
            fontSize="1em"
            onChange={searchHandler}
            h="100%"
            type="text"
            border="none"
            {...inputProps}
            ref={register({ required: error_msg })}
          />
          <InputRightElement
            cursor="pointer"
            type="button"
            as="button"
            onClick={clear}
            _hover={{ bg: "outline.light" }}
            hidden={!expanded}
            zIndex={1}
            h="100%"
          >
            <IconButton
              minW="0"
              fontSize="200"
              aria-label="Clear"
              variant="link"
              icon={<CrossIcon />}
            />
          </InputRightElement>
          <input
            type="hidden"
            name={inputProps.name + "_id"}
            ref={register({ required: error_msg })}
          />
        </InputGroup>
        <Box
          minH="50px"
          transformOrigin="50% 0"
          bg="white"
          boxShadow="elevation_3"
          borderRadius="base"
          overflowY="auto"
          transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
          className="results-autocomplete scrollbar"
          {...resultProps}
          pos="absolute"
          top="calc(100% + 1em)"
          left="0"
          right="0"
        >
          {searching ? (
            <Flex w="100%" justifyContent="center" alignItems="center" h="50px">
              <Spinner />
            </Flex>
          ) : (
            <>
              {results.map((location: Location, index) => {
                return (
                  <Flex
                    justifyContent="flex-start"
                    cursor="pointer"
                    textAlign="center"
                    as="li"
                    tabIndex={0}
                    px={3}
                    py={4}
                    _hover={{ bg: "outline.light" }}
                    fontSize="1em"
                    alignItems="center"
                    onClick={selectHandler}
                    data-value={location._id.$oid}
                    data-title={`${trimCityEmpty(location.city)}${
                      location.country
                    }`}
                    key={index}
                  >
                    <LocationIcon mr={1} color="text.light" />
                    <Text color="text.dark" mr={1} fontWeight="400" as="h5">
                      <LocationTextIntl location={location} />
                    </Text>
                    <CountryFlagsLazy
                      h="1.5rem"
                      w="1.5rem"
                      ml="auto"
                      borderRadius="8px"
                      code={location.code}
                      country={location.country}
                    />
                  </Flex>
                )
              })}
            </>
          )}
        </Box>
        <Portal>
          <Box
            onClick={() => {
              setExpanded(false)
            }}
            transition="-ms-transform 150ms,-webkit-transform 150ms,transform 150ms, opacity 50ms 20ms, visibility 0ms 25ms !important;"
            aria-expanded={expanded}
            _expanded={{ d: "block" }}
            d="none"
            h="100vh"
            w="100%"
            bg="rgba(0,0,0,0.02)"
            pos="fixed"
          ></Box>
        </Portal>
      </Box>
    )
  }
)
